import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router";
import moment from "moment";

import { LabeledValue } from "components/designV2/labeledValue/labeledValue";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { useAlertStore } from "stores/alertStore/alertStore";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import {
  DetailsContainer,
  ObIconButton,
  ObSectionTitle,
  ObSectionTitleOffset,
  ObSettingActionsCell,
  ObSettingCell,
  ObSettingRow,
  ObSettingsButtonSlot,
  ObSettingsDescription,
  ObSettingsHeader,
  ObSettingsList,
  ObSettingsTitle,
  ObSettingsTitleGroup,
} from "./obSettings.styled";
import { CustomIcon } from "components/icon/customIcon.component";
import { ColorV2 } from "config/colors";
import { ObSettingsForm } from "./obSettingsForm";
import { OBSettingShortWeekDay } from "model/Company";
import type { DisplayObSetting } from "./obSettings.logic";
import { useObSettings } from "./useObSettings.hook";

export const ObSettings: React.FC = () => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const location = useLocation();

  const [showObForm, setShowObForm] = useState(false);
  const [companyState] = useCompanyStore();
  const [, , alertDispatch] = useAlertStore();
  const { dateList, deleteObSettings, weekList } = useObSettings();

  const hasObModule = useMemo(
    () =>
      companyState.companySettings?.find(
        (setting: { key: string; value: string }) =>
          setting.key === "CompanyShowInconvenientModule",
      )?.value === "true",
    [companyState.companySettings],
  );

  const showObError = (): void => {
    alertDispatch({
      type: "SHOW_ALERT",
      payload: {
        icon: "alert",
        title: t("Alerts.OhNo"),
        message: t("General.ErrorOccurred"),
      },
    });
  };

  useEffect(() => {
    if (hasObModule) return;

    const params = new URLSearchParams(location.search);
    params.set("page", "Subscription");

    history.replace({
      pathname: location.pathname,
      search: params.toString(),
    });
  }, [hasObModule, history, location.pathname, location.search]);
  const handleDeleteObSetting = async (
    sameSettingsList: readonly DisplayObSetting[],
  ): Promise<void> => {
    const status = await deleteObSettings(sameSettingsList);

    if (status !== "success") {
      showObError();
    }
  };

  const formatWeekdayValue = (
    sameSettingsList: readonly DisplayObSetting[],
  ): string =>
    sameSettingsList
      .map((setting) =>
        typeof setting.weekday === "number"
          ? (() => {
              const label = t(
                `WeekDays.${OBSettingShortWeekDay[setting.weekday]}`,
              ).toLocaleLowerCase(i18n.language);

              return `${label.charAt(0).toLocaleUpperCase(i18n.language)}${label.slice(1)}`;
            })()
          : null,
      )
      .filter(Boolean)
      .join(", ");

  if (!hasObModule) return null;

  return (
    <>
      <ObSettingsHeader>
        <ObSettingsTitleGroup>
          <ObSettingsTitle>
            {t("ProfileCompany.OBInconvenientWorkingHours")}
          </ObSettingsTitle>
          <ObSettingsDescription>
            {t("ProfileCompany.ObInfo")}
          </ObSettingsDescription>
        </ObSettingsTitleGroup>

        {!showObForm && (
          <ObSettingsButtonSlot>
            <PrimaryButton
              label={t("ProfileCompany.AddUnsocialHours")}
              iconName="v2/plus"
              onClick={() => setShowObForm(true)}
            />
          </ObSettingsButtonSlot>
        )}
      </ObSettingsHeader>

      {!showObForm && <DetailsContainer />}

      <ObSettingsForm setShowObForm={setShowObForm} showObForm={showObForm} />

      {weekList.length > 0 && (
        <ObSectionTitle>{t("ProfileCompany.Weekdays")}</ObSectionTitle>
      )}

      {weekList.length > 0 && (
        <ObSettingsList>
          {weekList.map((sameSettingsList, index) => (
            <ObSettingRow key={`week-${index}`}>
              <ObSettingCell>
                <LabeledValue
                  label={t("ProfileCompany.Days")}
                  value={formatWeekdayValue(sameSettingsList)}
                  variant="small"
                />
              </ObSettingCell>

              <ObSettingCell>
                <LabeledValue
                  label={t("ProfileCompany.Times")}
                  value={`${moment(
                    sameSettingsList[0].startTime,
                    "HH:mm:ss",
                  ).format("HH:mm")} - ${moment(
                    sameSettingsList[0].endTime,
                    "HH:mm:ss",
                  )
                    .add(1, "second")
                    .format("HH:mm")}`}
                  variant="small"
                />
              </ObSettingCell>

              <ObSettingCell>
                <LabeledValue
                  label={t("ProfileCompany.Compensation")}
                  value={
                    sameSettingsList[0].fixedValue != null
                      ? `${sameSettingsList[0].fixedValue} ${t("ProfileCompany.Kr")}`
                      : `${sameSettingsList[0].percentValue}%`
                  }
                  variant="small"
                />
              </ObSettingCell>

              <ObSettingActionsCell>
                <ObIconButton
                  type="button"
                  aria-label={t("General.Delete")}
                  onClick={() => {
                    void handleDeleteObSetting(sameSettingsList);
                  }}
                >
                  <CustomIcon
                    name="v2/trash"
                    size="20px"
                    color={ColorV2.NegativeRed}
                  />
                </ObIconButton>
              </ObSettingActionsCell>
            </ObSettingRow>
          ))}
        </ObSettingsList>
      )}

      {dateList.length > 0 && (
        <ObSectionTitleOffset>{t("ProfileCompany.Dates")}</ObSectionTitleOffset>
      )}

      {dateList.length > 0 && (
        <ObSettingsList>
          {dateList.map((sameSettingsList, index) => (
            <ObSettingRow key={`date-${index}`}>
              <ObSettingCell>
                <LabeledValue
                  label={t("ProfileCompany.Date")}
                  value={sameSettingsList[0].date ?? ""}
                  variant="small"
                />
              </ObSettingCell>

              <ObSettingCell>
                <LabeledValue
                  label={t("ProfileCompany.Times")}
                  value={`${moment(
                    sameSettingsList[0].startTime,
                    "HH:mm:ss",
                  ).format("HH:mm")} - ${moment(
                    sameSettingsList[0].endTime,
                    "HH:mm:ss",
                  )
                    .add(1, "second")
                    .format("HH:mm")}`}
                  variant="small"
                />
              </ObSettingCell>

              <ObSettingCell>
                <LabeledValue
                  label={t("ProfileCompany.Compensation")}
                  value={
                    sameSettingsList[0].fixedValue != null
                      ? `${sameSettingsList[0].fixedValue} ${t("ProfileCompany.Kr")}`
                      : `${sameSettingsList[0].percentValue}%`
                  }
                  variant="small"
                />
              </ObSettingCell>

              <ObSettingActionsCell>
                <ObIconButton
                  type="button"
                  aria-label={t("General.Delete")}
                  onClick={() => {
                    void handleDeleteObSetting(sameSettingsList);
                  }}
                >
                  <CustomIcon
                    name="v2/trash"
                    size="20px"
                    color={ColorV2.NegativeRed}
                  />
                </ObIconButton>
              </ObSettingActionsCell>
            </ObSettingRow>
          ))}
        </ObSettingsList>
      )}
    </>
  );
};
